import {ApiClient as Api} from '@hconnect/apiclient'
import {KpiCalculationTimeFrameDto, KpiType, KpiVisibilityFlag} from '@hconnect/common/types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import qs from 'qs'
import {useEffect} from 'react'

import {mapKpiValueTypeToKpiData} from '../../mappers'
import {KpisQueryKey, type KpiData, type KpiValueTypeDto} from '../../types'

type KpisByTypesSearchParams = {
  plantId: string
  kpiVisibilityFlag: KpiVisibilityFlag
  timeFrame?: KpiCalculationTimeFrameDto
  kpiTypes: KpiType[]
}

const QueryKeyPrefix = KpisQueryKey.KpisByTypes

export const useGetKpisByTypes = <TSelect = KpiData[]>(
  {plantId, timeFrame, kpiVisibilityFlag, kpiTypes}: KpisByTypesSearchParams,
  options?: Omit<UseQueryOptions<KpiValueTypeDto, AxiosError, TSelect>, 'queryKey' | 'queryFn'> & {
    onError?: (error: AxiosError) => void
  }
) => {
  const from = timeFrame?.startDate
  const to = timeFrame?.endDate
  const calculationTimeFrameType = timeFrame?.calculationTimeFrameType

  const query = useQuery<KpiValueTypeDto, AxiosError, TSelect>({
    queryKey: [QueryKeyPrefix, plantId, from, to, calculationTimeFrameType, kpiTypes],
    queryFn: async ({signal}) => {
      const path = `/kpi-result/${plantId}`
      const response = await Api.axiosInstance.get<KpiValueTypeDto>(path, {
        params: {
          from,
          to,
          kpiVisibilityFlags: [kpiVisibilityFlag],
          calculationTimeFrameType,
          kpiTypes: kpiTypes
        },
        paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat'}),
        signal
      })

      return response.data
    },
    enabled: !!from && !!to && !!timeFrame,
    select: mapKpiValueTypeToKpiData as unknown as (data: KpiValueTypeDto | undefined) => TSelect,
    ...options
  })

  const onError = options?.onError

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
